import { useRef, useEffect, useLayoutEffect, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, MessageCircle, Loader2, Users, ChevronDown, ChevronUp, MapPin, Calendar, ExternalLink, X } from 'lucide-react';
import type { ConversationData, MessageData, PendingMessage, ChatUser } from './types';
import { getConversationTitle, getConversationSubtitle, getConversationIcon } from './utils';
import { MessageBubble } from './MessageBubble';
import { PendingMessageBubble } from './PendingMessageBubble';
import { MessageInput } from './MessageInput';
import { useChatTableInfo } from '@/lib/api/useChat';
import { useUpdateBuddyApplicationStatus } from '@/lib/api/useEventBuddies';
import type { UserAPI } from '@shared/types/api';
import { formatDateToFull, formatTimeToDisplay } from '@/utils/dateUtils';
import barBgUrl from '@/assets/images/img_bar.webp';
import dinnerBgUrl from '@/assets/images/img_dinner.webp';

// How close to the end still counts as "reading the newest message", and so
// still follows new arrivals. Generous enough to survive the couple of pixels
// a rounding difference or an elastic overscroll leaves behind.
const NEAR_BOTTOM_PX = 120;
// How close to the top starts fetching the previous page.
const LOAD_OLDER_PX = 250;

// Unified shape for the header expandable details, populated from either the
// derived table info (postgres-backed) or the manually-set chatInfo (Firestore).
interface ChatHeaderInfo {
  venueName: string;
  subtitle: string;
  address: string | null;
  googleMapsLink: string | null;
  eventDate: string | null; // YYYY-MM-DD (local)
  eventTime: string | null; // HH:MM (local)
  attendees: UserAPI.ChatTableAttendee[];
  description: string | null;
  eventLink: string | null;
  backgroundUrl: string;
}

// Convert an ISO 8601 datetime to local YYYY-MM-DD + HH:MM parts so we can
// reuse the date formatters that expect those shapes.
function isoToLocalDateTimeParts(iso: string): { date: string; time: string } | null {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

interface ConversationViewProps {
  conversation: ConversationData | null;
  messages: MessageData[];
  pendingMessages: PendingMessage[];
  userId: string | null;
  userCache: Record<string, ChatUser>;
  messageText: string;
  /**
   * Unused: the scroll logic keys off the `messages` array itself, which is a
   * new array on every snapshot, rather than off a separately-tracked id that
   * could disagree with what is actually rendered. Kept on the props so the
   * chat page can go on passing what its context exposes.
   */
  newestMessageId?: string | null;
  onMessageTextChange: (text: string) => void;
  onSendMessage: () => void;
  onRetryMessage: (message: PendingMessage) => void;
  onDismissMessage: (messageId: string) => void;
  onBack: () => void;
  onLoadMore?: () => void;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
}

export function ConversationView({
  conversation,
  messages,
  pendingMessages,
  userId,
  userCache,
  messageText,
  onMessageTextChange,
  onSendMessage,
  onRetryMessage,
  onDismissMessage,
  onBack,
  onLoadMore,
  hasMoreMessages = false,
  isLoadingMore = false,
}: ConversationViewProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const tableId = conversation?.tableId;
  const { data: tableInfo } = useChatTableInfo(tableId);
  const showTableInfoCard = !!tableId && tableInfo?.isClosed;

  // Prefer the derived table info (when the table is closed). Otherwise fall
  // back to the manually-set chatInfo on the conversation doc.
  const headerInfo: ChatHeaderInfo | null = useMemo(() => {
    const backgroundUrl = conversation?.tableId ? dinnerBgUrl : barBgUrl;
    if (showTableInfoCard && tableInfo) {
      return {
        venueName: tableInfo.restaurantName,
        subtitle: `${tableInfo.restaurantName} – ${tableInfo.reservationName?.trim() || 'Table'}`,
        address: tableInfo.address,
        googleMapsLink: tableInfo.googleMapsLink,
        eventDate: tableInfo.eventDate || null,
        eventTime: tableInfo.eventTime || null,
        attendees: tableInfo.attendees,
        description: null,
        eventLink: null,
        backgroundUrl,
      };
    }
    if (conversation?.chatInfo) {
      const parts = isoToLocalDateTimeParts(conversation.chatInfo.meetingTime);
      return {
        venueName: conversation.chatInfo.venue.name,
        subtitle: conversation.chatInfo.venue.name,
        address: conversation.chatInfo.venue.address,
        googleMapsLink: conversation.chatInfo.venue.googleMapsLink,
        eventDate: parts?.date ?? null,
        eventTime: parts?.time ?? null,
        attendees: [],
        description: conversation.chatInfo.description,
        eventLink: conversation.chatInfo.eventLink ?? null,
        backgroundUrl,
      };
    }
    return null;
  }, [showTableInfoCard, tableInfo, conversation?.tableId, conversation?.chatInfo]);

  // Whether the venue / meeting details panel under the title is open. Lifted
  // out of the header only because the two rows are siblings.
  const [detailsExpanded, setDetailsExpanded] = useState(true);

  // The message list is the one scroller on this screen.
  //
  // What was here before decided, on every single call, whether to measure the
  // list or the window — by asking whether the list had overflowed yet. That
  // answer flips as messages arrive and as the keyboard opens, so "am I at the
  // bottom", "should I load older" and "put the scroll back where it was" could
  // each be talking about a different element from one moment to the next; on
  // mobile the window branch was measuring the page *behind* the chat. The
  // column now has a definite height (see `routeOwnsViewport` in MainApp), so
  // this element always overflows and is always the thing that moves.
  const scrollerRef = useRef<HTMLDivElement>(null);

  // True while the reader is parked at the newest message — the only state in
  // which an arriving message should move the view out from under them. A ref,
  // not state: it is read from scroll handlers and never rendered.
  const stickToBottomRef = useRef(true);
  // Scroll height measured just before an older page is prepended, so the
  // restore below can subtract whatever appeared above the reader.
  const restoreFromHeightRef = useRef<number | null>(null);
  const loadingOlderRef = useRef(false);
  // The first paint of a conversation jumps to the bottom; everything after it
  // animates there.
  const hasPaintedRef = useRef(false);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // A different conversation is a different reading position.
  useLayoutEffect(() => {
    stickToBottomRef.current = true;
    restoreFromHeightRef.current = null;
    loadingOlderRef.current = false;
    hasPaintedRef.current = false;
  }, [conversation?.id]);

  // Hold the reading position as the list changes underneath it. Layout, not
  // effect: this runs before the browser paints, so the correction is never a
  // visible jump.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const before = restoreFromHeightRef.current;
    if (before !== null) {
      // An older page landed on top: move down by exactly the height it added,
      // so the message the reader was looking at stays under their eyes.
      restoreFromHeightRef.current = null;
      el.scrollTop += el.scrollHeight - before;
      return;
    }

    if (!stickToBottomRef.current) return;
    if (messages.length === 0 && pendingMessages.length === 0) return;
    const smooth = hasPaintedRef.current;
    hasPaintedRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, [messages, pendingMessages.length]);

  // The list also changes height for reasons that have nothing to do with
  // messages — the details panel expanding, the keyboard taking half the
  // screen, web fonts landing. While sticking to the bottom, follow it.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current || restoreFromHeightRef.current !== null) return;
      el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [conversation?.id]);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;

    if (loadingOlderRef.current || isLoadingMore || !hasMoreMessages || !onLoadMore) return;
    if (el.scrollTop > LOAD_OLDER_PX) return;
    loadingOlderRef.current = true;
    restoreFromHeightRef.current = el.scrollHeight;
    onLoadMore();
  }, [onLoadMore, hasMoreMessages, isLoadingMore]);

  useEffect(() => {
    if (isLoadingMore) return;
    loadingOlderRef.current = false;
    // A page that came back empty prepends nothing, so the pending restore has
    // nothing to correct against — dropping it here keeps it from firing
    // against whatever message arrives next.
    restoreFromHeightRef.current = null;
  }, [isLoadingMore]);

  // Focus the composer when a conversation opens — on a wide screen only. On a
  // phone this summons the keyboard over half the screen before the reader has
  // seen a single message.
  useEffect(() => {
    if (!conversation) return;
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [conversation]);

  const onTapHeader = useCallback(() => {
    scrollToBottom(true);
  }, [scrollToBottom]);

  if (!conversation) {
    return <EmptyConversationState />;
  }

  // Three rows: a pinned header, the scrolling list, a pinned composer. Both
  // bars were `fixed` before — positioned against the window rather than this
  // pane, which is why the list needed a JS-measured top padding to sit under
  // the header, why that measurement ran *during render* (a setState in the
  // render path), and why the composer had to guess the list column's width.
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Conversation Header (includes venue/meeting details when available) */}
      <ConversationHeader
        conversation={conversation}
        userId={userId}
        userCache={userCache}
        headerInfo={headerInfo}
        detailsExpanded={detailsExpanded}
        onToggleExpanded={() => setDetailsExpanded((v) => !v)}
        onBack={onBack}
        onTap={onTapHeader}
      />

      {/* Kiki request banner (accept / decline / accepted notice) */}
      {conversation.kind === 'kiki_request' && conversation.kikiRequestInfo && (
        <KikiRequestBanner conversation={conversation} userId={userId} onBack={onBack} />
      )}

      {/* Messages */}
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-4"
      >
        {/* Loading more indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
          </div>
        )}

        {/* Load more hint - only show if there are more messages and not loading */}
        {hasMoreMessages && !isLoadingMore && messages.length > 0 && (
          <div className="flex justify-center py-2">
            <span className="text-xs text-dark-muted">Scroll up to load more</span>
          </div>
        )}

        {messages.map((message, index) => {
          const next = index < messages.length - 1 ? messages[index + 1] : null;
          const isLastInGroup =
            message.type === 'system'
              ? !next || next.type !== 'system'
              : !next ||
                next.type === 'system' ||
                next.senderId !== message.senderId;

          return (
            <MessageBubble
              key={message.id}
              message={message}
              userId={userId}
              userCache={userCache}
              isLastInGroup={isLastInGroup}
            />
          );
        })}

        {/* Pending messages (optimistic updates) */}
        {pendingMessages.map((message) => (
          <PendingMessageBubble
            key={`pending-${message.id}`}
            message={message}
            onRetry={onRetryMessage}
            onDismiss={onDismissMessage}
          />
        ))}
      </div>

      {/* Message Input */}
      <MessageInput
        ref={inputRef}
        value={messageText}
        onChange={onMessageTextChange}
        onSend={onSendMessage}
      />
    </div>
  );
}

interface ConversationHeaderProps {
  conversation: ConversationData;
  userId: string | null;
  userCache: Record<string, ChatUser>;
  headerInfo: ChatHeaderInfo | null;
  detailsExpanded: boolean;
  onToggleExpanded: () => void;
  onBack: () => void;
  onTap: () => void;
}

function getKikiPageLink(conversation: ConversationData): string | null {
  // Kikis stand on their own URL — the request id is all that's needed.
  if (conversation.kind === 'kiki_request' && conversation.kikiRequestInfo) {
    return `/kiki/${conversation.kikiRequestInfo.requestId}`;
  }
  if (conversation.kind === 'kiki') {
    // conversation.id = "kiki_${requestId}"
    const requestId = conversation.id.startsWith('kiki_') ? conversation.id.slice(5) : null;
    if (requestId) return `/kiki/${requestId}`;
  }
  return null;
}

function ConversationHeader({
  conversation,
  userId,
  userCache,
  headerInfo,
  detailsExpanded,
  onToggleExpanded,
  onTap,
  onBack,
}: ConversationHeaderProps) {
  const subtitle = getConversationSubtitle(conversation);
  const Icon = getConversationIcon(conversation);
  const kikiPageLink = getKikiPageLink(conversation);
  const showDetails = !!headerInfo || !!kikiPageLink;

  const handleHeaderClick = () => {
    if (showDetails) {
      onToggleExpanded();
    } else {
      onTap();
    }
  };

  return (
    // The safe-area padding is on the bar itself, so its background reaches
    // under the status bar rather than leaving a bare strip above it. Mobile
    // only: on md+ the app's own nav is above this and has already paid it.
    // Capped at half the pane because the details panel is open by default and
    // holds free text: a long venue description used to overlay the messages,
    // and in the column it would push them out instead. Past the cap the header
    // scrolls itself and the conversation keeps its half of the screen.
    <div
      className="flex-none max-h-[50%] overflow-y-auto overscroll-contain relative z-10 bg-white/80 backdrop-blur-sm dark:bg-neutral-900/80 max-md:top-safe-padding border-b border-dark-border dark:border-neutral-800"
    >
      <div
        className={`p-4 flex items-center gap-3 ${showDetails ? 'cursor-pointer' : ''}`}
        onClick={handleHeaderClick}
        role={showDetails ? 'button' : undefined}
        aria-expanded={showDetails ? detailsExpanded : undefined}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBack();
          }}
          className="md:hidden p-2 -ml-2 hover:bg-dark-card rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="w-10 h-10 rounded-full bg-primary-200 dark:bg-primary-900/40 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-primary-700 dark:text-primary-400" />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold truncate">
            {getConversationTitle(conversation, userId, userCache)}
          </h4>
          <p className="text-xs text-dark-muted truncate">
            {headerInfo
              ? headerInfo.subtitle
              : subtitle || (conversation.kind === 'group' ? '' : `${conversation.participants.length} participants`)}
          </p>
        </div>

        {headerInfo?.googleMapsLink && (
          <a
            href={headerInfo.googleMapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-dark-card dark:hover:bg-neutral-700/50 transition-colors text-primary-600 dark:text-primary-400"
            title="View on Google Maps"
            onClick={(e) => e.stopPropagation()}
          >
            <MapPin className="w-5 h-5" />
          </a>
        )}

        {showDetails && (
          <span className="p-2 -mr-2 shrink-0" aria-hidden="true">
            {detailsExpanded ? (
              <ChevronUp className="w-5 h-5 text-dark-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-dark-muted" />
            )}
          </span>
        )}
      </div>

      {kikiPageLink && (
        <KikiInfoRow conversation={conversation} kikiPageLink={kikiPageLink} expanded={detailsExpanded} />
      )}

      {headerInfo && (
        <ChatHeaderDetails
          info={headerInfo}
          expanded={detailsExpanded}
        />
      )}
    </div>
  );
}

function KikiInfoRow({
  conversation,
  kikiPageLink,
  expanded,
}: {
  conversation: ConversationData;
  kikiPageLink: string;
  expanded: boolean;
}) {
  let venueName: string | null = null;
  let date: string | null = null;
  let coverImageUrl: string | null = null;

  if (conversation.kind === 'kiki_request' && conversation.kikiRequestInfo) {
    venueName = conversation.kikiRequestInfo.venueName;
    date = conversation.kikiRequestInfo.eventDate;
    coverImageUrl = conversation.kikiRequestInfo.coverImageUrl ?? null;
  } else if (conversation.kind === 'kiki' && conversation.chatInfo) {
    venueName = conversation.chatInfo.venue.name;
    const parts = isoToLocalDateTimeParts(conversation.chatInfo.meetingTime);
    date = parts?.date ?? null;
    coverImageUrl = conversation.coverImageUrl ?? null;
  }

  return (
    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <a
            href={`/app${kikiPageLink}`}
            className="flex items-center gap-2.5 px-4 py-2.5 border-t border-dark-border dark:border-neutral-800 hover:bg-base-200/60 dark:hover:bg-neutral-800/40 transition-colors no-underline group"
            onClick={(e) => e.stopPropagation()}
          >
            {coverImageUrl ? (
              <img
                src={coverImageUrl}
                alt=""
                className="w-9 h-9 rounded-md object-cover shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-md bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-primary-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {venueName && (
                <p className="text-xs font-medium text-dark truncate">{venueName}</p>
              )}
              {date && (
                <p className="text-xs text-dark-muted truncate">{formatDateToFull(date)}</p>
              )}
            </div>
            <span className="text-xs text-primary-500 group-hover:text-primary-600 transition-colors shrink-0 flex items-center gap-1">
              View kiki <ExternalLink className="w-3 h-3" />
            </span>
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ChatHeaderDetails({
  info,
  expanded,
}: {
  info: ChatHeaderInfo;
  expanded: boolean;
}) {
  const trimmedAddress = info.address?.trim();
  const trimmedDescription = info.description?.trim();
  const hasEvent = !!(info.eventDate && info.eventTime);
  const hasAddress = !!trimmedAddress;
  const hasAttendees = info.attendees.length > 0;
  const hasDescription = !!trimmedDescription;
  const hasEventLink = !!info.eventLink;
  const hasAny = hasEvent || hasAddress || hasAttendees || hasDescription || hasEventLink;

  return (
    <AnimatePresence initial={false}>
      {expanded && hasAny && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden relative bg-cover bg-center dark:text-white"
        >
          <div className="absolute inset-0 bg-neutral-100/50 dark:bg-neutral-900/50" aria-hidden="true" />
          <div className="relative px-4 py-3.5 space-y-3.5">
            {trimmedDescription && (
              <p className="text-sm text-dark-muted leading-snug whitespace-pre-wrap">
                {trimmedDescription}
              </p>
            )}
            {info.eventDate && info.eventTime && (
              <div className="flex gap-3 items-start">
                <Calendar className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm text-dark-muted leading-snug min-w-0">
                  {formatDateToFull(info.eventDate)} at {formatTimeToDisplay(info.eventTime)}
                </p>
              </div>
            )}
            {trimmedAddress && (
              <div className="flex gap-3 items-start">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm leading-snug min-w-0">
                  {trimmedAddress}
                </p>
              </div>
            )}
            {hasAttendees && (
              <div className="flex gap-3 items-start">
                <Users className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm min-w-0">
                  {info.attendees.map((a: UserAPI.ChatTableAttendee) => a.displayName).join(', ')}
                </p>
              </div>
            )}
            {info.eventLink && (
              <div className="flex gap-3 items-start">
                <ExternalLink className="w-4 h-4 mt-0.5 shrink-0" />
                <a
                  href={info.eventLink}
                  className="text-sm leading-snug min-w-0 underline underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  View event
                </a>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function KikiRequestBanner({
  conversation,
  userId,
  onBack,
}: {
  conversation: ConversationData;
  userId: string | null;
  onBack: () => void;
}) {
  const info = conversation.kikiRequestInfo!;
  const isOwner = userId === info.ownerUserId;
  const [, navigate] = useLocation();
  const update = useUpdateBuddyApplicationStatus(
    info.eventSlug ?? '',
    info.requestId,
  );

  const handleAccept = async () => {
    if (!info.eventSlug) return;
    await update.mutateAsync({ applicationId: info.applicationId, status: 'accepted' });
    navigate(`/chat/kiki_${info.requestId}`);
  };

  const handleDecline = async () => {
    if (!info.eventSlug) return;
    await update.mutateAsync({ applicationId: info.applicationId, status: 'declined' });
    onBack();
  };

  return (
    // A row in the column, so it needs no knowledge of the header's height —
    // which used to be measured in JS and passed down as a `top` offset.
    <div className="flex-none relative z-9 bg-white/90 backdrop-blur-sm dark:bg-neutral-900/90 border-b border-dark-border dark:border-neutral-800">
      {info.status === 'pending' && isOwner && (
        <div className="px-4 py-2.5 flex items-center gap-3">
          <p className="flex-1 text-sm text-neutral-600 dark:text-neutral-300 min-w-0 truncate">
            Wants to kiki at <span className="font-medium">{info.venueName}</span>
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleDecline}
              disabled={update.isPending}
              className="p-1.5 rounded-full text-neutral-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
              aria-label="Decline"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={update.isPending}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {update.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Accept
            </button>
          </div>
        </div>
      )}
      {info.status === 'pending' && !isOwner && (
        <div className="px-4 py-2.5 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 text-neutral-400" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Waiting for a response…</p>
        </div>
      )}
      {info.status === 'accepted' && (
        <div className="px-4 py-2.5 flex items-center gap-2">
          <Check className="w-3.5 h-3.5 text-primary-500 shrink-0" />
          <p className="text-sm font-medium text-primary-600 dark:text-primary-400 flex-1 min-w-0 truncate">
            Kiki accepted!
          </p>
          <a
            href={`/app/chat/kiki_${info.requestId}`}
            className="text-xs text-primary-500 hover:text-primary-600 transition-colors shrink-0"
          >
            Open group chat →
          </a>
        </div>
      )}
      {info.status === 'declined' && (
        <div className="px-4 py-2.5 flex items-center gap-2">
          <X className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">This kiki didn't work out.</p>
        </div>
      )}
    </div>
  );
}

function EmptyConversationState() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="hidden md:flex flex-1 items-center justify-center"
    >
      <div className="text-center">
        <MessageCircle className="w-16 h-16 text-dark-muted mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Select a Conversation</h2>
        <p className="text-dark-muted">Choose a chat from the list to start messaging</p>
      </div>
    </motion.div>
  );
}
